<template>
  <div class="w-full max-w-4xl mx-auto px-4 pt-16 sm:pt-20">
    <div class="text-center">
      <p class="text-neutral-600">Bienvenido, {{ userName }}</p>
      <h1 class="text-2xl sm:text-3xl font-semibold text-neutral-800 mt-2">
        ¿Con qué rol quieres entrar hoy?
      </h1>
    </div>

    <div class="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div
        v-for="role in roleOptions"
        :key="role.key"
        class="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 flex flex-col items-center text-center"
      >
        <div class="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center">
          <Icon :icon="role.icon" height="30" class="text-blue-600" />
        </div>
        <h2 class="text-lg font-semibold text-neutral-800 mt-4">{{ role.title }}</h2>
        <p class="text-sm text-neutral-500 mt-2">{{ role.description }}</p>
        <button
          class="mob-btn mob-cta-gold w-full mt-6"
          @click="handleRoleSelect(role.key)"
        >
          {{ role.buttonText }}
        </button>
      </div>
    </div>

    <div
      v-if="showInstitutionModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-2xl shadow-lg w-full max-w-sm p-6">
        <template v-if="!showCreateInstitutionForm">
          <h3 class="text-lg font-semibold text-neutral-800">Selecciona una institución</h3>
          <select
            v-model="selectedInstitutionId"
            class="mt-4 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-700 bg-white"
          >
            <option
              v-for="institution in activeInstitutions"
              :key="institution.id"
              :value="institution.id"
            >
              {{ institution.name }}
            </option>
          </select>
          <button
            class="mob-btn mob-cta-gold w-full mt-4"
            :disabled="!selectedInstitutionId || isLoadingInstitutions"
            @click="confirmInstitution"
          >
            Continuar
          </button>
          <button
            class="w-full mt-3 text-sm text-blue-600 hover:underline"
            type="button"
            @click="openCreateInstitution"
          >
            Crear nueva institución
          </button>
        </template>

        <template v-else>
          <h3 class="text-lg font-semibold text-neutral-800">Crear nueva institución</h3>
          <div class="mt-4 space-y-4">
            <div>
              <label class="block text-sm text-neutral-600 mb-2">Nombre de la institución</label>
              <input
                v-model.trim="createForm.name"
                type="text"
                class="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-700"
                placeholder="Ej: Colegio San Martín"
              />
              <p v-if="createError" class="mt-1 text-sm text-red-600">{{ createError }}</p>
            </div>
            <div>
              <label class="block text-sm text-neutral-600 mb-2">Código (opcional)</label>
              <input
                v-model.trim="createForm.code"
                type="text"
                class="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-700"
                placeholder="Ej: CSM-01"
              />
            </div>
          </div>
          <div class="flex justify-between items-center mt-6">
            <button
              type="button"
              class="text-sm text-neutral-600 hover:underline"
              @click="closeCreateInstitution"
            >
              Volver
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-md text-white bg-blue-600 disabled:opacity-50"
              :disabled="createForm.name.length === 0 || isCreating"
              @click="submitCreateInstitution"
            >
              {{ isCreating ? "Creando…" : "Crear institución" }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue2";
import { getMyInstitutions, createMyInstitution } from "@/services/institutions.service";

export default {
  name: "RoleSelect",
  components: {
    Icon,
  },
  data() {
    return {
      userName: "Usuario",
      roleOptions: [],
      institutions: [],
      selectedInstitutionId: null,
      showInstitutionModal: false,
      isLoadingInstitutions: false,
      showCreateInstitutionForm: false,
      isCreating: false,
      createForm: {
        name: "",
        code: "",
      },
      createError: "",
    };
  },
  methods: {
    handleRoleSelect(roleKey) {
      localStorage.setItem("role", roleKey);
      if (roleKey === "ROLE_ACADEMIC_ADMIN") {
        this.handleAcademicAdminEntry();
        return;
      }
      this.$router.push("/dashboard");
    },
    async handleAcademicAdminEntry() {
      this.isLoadingInstitutions = true;
      try {
        const list = await getMyInstitutions();
        this.institutions = Array.isArray(list) ? list : [];
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));

        if (this.activeInstitutions.length === 1) {
          this.selectedInstitutionId = this.activeInstitutions[0].id;
          localStorage.setItem("teacher_current_institution_id", this.selectedInstitutionId);
          this.$router.push("/teacher/groups");
          return;
        }

        if (this.activeInstitutions.length > 1) {
          const storedId = localStorage.getItem("teacher_current_institution_id");
          this.selectedInstitutionId = storedId ? Number(storedId) : this.activeInstitutions[0].id;
          this.showInstitutionModal = true;
          return;
        }
      } catch (error) {
        console.error("Error cargando instituciones", error);
      } finally {
        this.isLoadingInstitutions = false;
      }

      this.$router.push("/teacher/groups");
    },
    confirmInstitution() {
      if (!this.selectedInstitutionId) return;
      localStorage.setItem("teacher_current_institution_id", this.selectedInstitutionId);
      this.showInstitutionModal = false;
      this.$router.push("/teacher/groups");
    },
    openCreateInstitution() {
      this.showCreateInstitutionForm = true;
    },
    closeCreateInstitution() {
      this.showCreateInstitutionForm = false;
      this.createError = "";
    },
    async submitCreateInstitution() {
      if (!this.createForm.name || !this.createForm.name.trim()) {
        this.createError = "El nombre de la institución es obligatorio.";
        return;
      }
      this.isCreating = true;
      this.createError = "";
      try {
        const created = await createMyInstitution({
          name: this.createForm.name.trim(),
          code: this.createForm.code ? this.createForm.code.trim() : null,
        });
        this.institutions = [...this.institutions, created];
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));
        this.selectedInstitutionId = created.id;
        localStorage.setItem("teacher_current_institution_id", created.id);
        this.showInstitutionModal = false;
        this.showCreateInstitutionForm = false;
        this.$router.push("/teacher/groups");
      } catch (error) {
        this.createError = "No se pudo crear la institución. Intenta nuevamente.";
        console.error("Error creando institución", error);
      } finally {
        this.isCreating = false;
      }
    },
    buildRoleOptions(roleNames) {
      const options = [];
      if (roleNames.includes("ROLE_STUDENT") || roleNames.includes("ROLE_ADMIN")) {
        options.push({
          key: "ROLE_STUDENT",
          title: "Estudiante",
          description: "Accede a tus cursos, lecciones y progreso.",
          buttonText: "Entrar como Estudiante",
          icon: "ph:student",
        });
      }
      if (roleNames.includes("ROLE_ACADEMIC_ADMIN")) {
        options.push({
          key: "ROLE_ACADEMIC_ADMIN",
          title: "Administrador académico",
          description: "Gestiona instituciones, grupos y estudiantes.",
          buttonText: "Entrar como Administrador",
          icon: "ph:buildings",
        });
      }
      return options;
    },
  },
  computed: {
    activeInstitutions() {
      return (this.institutions || []).filter((institution) => institution.active !== false);
    },
  },
  mounted() {
    const authInfo = JSON.parse(localStorage.getItem("auth") || "{}");
    this.userName = authInfo.data?.name || "Usuario";
    const roleNames = (authInfo.data?.roles || []).map((role) => role.name);
    this.roleOptions = this.buildRoleOptions(roleNames);

    if (this.roleOptions.length <= 1) {
      const fallbackRole = roleNames[0] || "ROLE_STUDENT";
      this.handleRoleSelect(fallbackRole);
    }
  },
};
</script>
